import {
  type AdminFeatureFlags,
  ApiError,
  ApiNetworkError,
  type ClientStatus,
  type ClientTenantPublic,
  adminAuthLogout,
  adminClientsList,
  adminClientsRetry,
  adminFeatureFlagsGet,
} from '@cortex/web-api/clients/admin/api';
import { FEATURE_FLAG_META } from '@cortex/web-api/clients/admin/feature-flags';
import { Wordmark } from '@cortex/web-api/ui/admin/Wordmark';
import { useEffect, useRef, useState } from 'react';
import { InvoiceUsage } from './InvoiceUsage.js';

/**
 * v1.2 client management — list of provisioned clients with live status.
 *
 * Polls /admin/clients every 3s while any row is in a transient state
 * (configuring or bootstrapping); switches to manual-refresh once
 * everything is in a terminal state (live | failed | archived).
 *
 * Per-row retry button is enabled only on 'failed' rows. Add new client
 * routes through the Onboard screen via onAddNewClient.
 */

interface Props {
  onSessionExpired: () => void;
  onLoggedOut: () => void;
  onAddNewClient: () => void;
  onSwitchToFleet: () => void;
  onSwitchToExecutions: () => void;
  /** Drilldown to TenantDetailScreen for the given slug. */
  onSelectTenant: (slug: string) => void;
}

type TenantFeatureState =
  | { status: 'loaded'; flags: AdminFeatureFlags }
  | { status: 'unavailable' };

export function ClientsScreen({
  onSessionExpired,
  onLoggedOut,
  onAddNewClient,
  onSwitchToFleet,
  onSwitchToExecutions,
  onSelectTenant,
}: Props) {
  const [rows, setRows] = useState<ClientTenantPublic[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retrying, setRetrying] = useState<string | null>(null);
  const [tenantFeatures, setTenantFeatures] = useState<Record<string, TenantFeatureState>>({});
  const loadGeneration = useRef(0);

  async function load() {
    const generation = ++loadGeneration.current;
    setLoading(true);
    setError(null);
    try {
      const res = await adminClientsList();
      if (generation === loadGeneration.current) setRows(res.clients);

      const featureResults = await Promise.all(
        res.clients.map(async (client) => {
          try {
            const flags = await adminFeatureFlagsGet(client.slug);
            return [client.slug, { status: 'loaded', flags }] as const;
          } catch (featureError) {
            if (featureError instanceof ApiError && featureError.status === 401) {
              throw featureError;
            }
            return [client.slug, { status: 'unavailable' }] as const;
          }
        }),
      );
      if (generation === loadGeneration.current) {
        setTenantFeatures(Object.fromEntries(featureResults));
      }
    } catch (err) {
      if (generation !== loadGeneration.current) return;
      if (err instanceof ApiError && err.status === 401) {
        onSessionExpired();
        return;
      }
      if (err instanceof ApiNetworkError) {
        setError("Couldn't reach the server.");
      } else {
        setError('Could not load clients.');
      }
    } finally {
      if (generation === loadGeneration.current) setLoading(false);
    }
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: load is stable; intentional one-shot on mount
  useEffect(() => {
    void load();
  }, []);

  // Poll while any row is transient.
  // biome-ignore lint/correctness/useExhaustiveDependencies: load is stable; only resubscribe when rows change
  useEffect(() => {
    if (!rows || loading) return;
    const transient = rows.some((r) => r.status === 'configuring' || r.status === 'bootstrapping');
    if (!transient) return;
    const t = setInterval(() => void load(), 3000);
    return () => clearInterval(t);
  }, [rows, loading]);

  async function handleRetry(slug: string) {
    setRetrying(slug);
    try {
      await adminClientsRetry(slug);
      await load();
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        onSessionExpired();
        return;
      }
      setError(err instanceof Error ? err.message : 'retry failed');
    } finally {
      setRetrying(null);
    }
  }

  async function handleSignOut() {
    await adminAuthLogout();
    onLoggedOut();
  }

  return (
    <div className="app">
      <header
        className="app-header"
        style={{ justifyContent: 'space-between', display: 'flex', flexWrap: 'wrap' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
          <img src="/acel-cortex-mark.svg" alt="" width={32} height={32} />
          <Wordmark />
        </div>
        <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
          <button
            type="button"
            onClick={onSwitchToFleet}
            className="btn-secondary"
            style={{ padding: '8px 16px', fontSize: 'var(--fs-small)' }}
          >
            fleet
          </button>
          <button
            type="button"
            onClick={onSwitchToExecutions}
            className="btn-secondary"
            style={{ padding: '8px 16px', fontSize: 'var(--fs-small)' }}
          >
            executions
          </button>
          <button
            type="button"
            onClick={handleSignOut}
            className="btn-secondary"
            style={{ padding: '8px 16px', fontSize: 'var(--fs-small)' }}
          >
            sign out
          </button>
        </div>
      </header>
      <main
        className="app-main"
        style={{ overflow: 'auto', padding: 'var(--space-8) var(--space-6)' }}
      >
        <div
          style={{
            maxWidth: 960,
            width: '100%',
            minWidth: 0,
            margin: '0 auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--space-6)',
          }}
        >
          <InvoiceUsage onSessionExpired={onSessionExpired} />
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <h1 className="h2" style={{ color: 'var(--ink)' }}>
              clients
            </h1>
            <button type="button" onClick={onAddNewClient} className="btn-primary">
              client onboarding
            </button>
          </div>

          {loading && rows === null ? (
            <p className="field-help">loading…</p>
          ) : error && rows === null ? (
            <div className="card" style={{ padding: 'var(--space-6)' }}>
              <p className="field-error">{error}</p>
              <button
                type="button"
                onClick={load}
                className="btn-secondary"
                style={{ marginTop: 'var(--space-4)' }}
              >
                retry
              </button>
            </div>
          ) : rows && rows.length === 0 ? (
            <div className="card" style={{ padding: 'var(--space-6)' }}>
              <p className="field-help">
                No clients yet. New clients appear after sales submits the connected onboarding
                form.
              </p>
            </div>
          ) : (
            <>
              {rows && rows.length > 0 && (
                <FeatureComparison clients={rows} tenantFeatures={tenantFeatures} />
              )}
              {rows?.map((c) => (
                <ClientRow
                  key={c.slug}
                  client={c}
                  onRetry={() => handleRetry(c.slug)}
                  onSelect={() => onSelectTenant(c.slug)}
                  retrying={retrying === c.slug}
                />
              ))}
            </>
          )}
        </div>
      </main>
      <footer className="app-footer">acel·cortex internal</footer>
    </div>
  );
}

function FeatureComparison({
  clients,
  tenantFeatures,
}: {
  clients: ClientTenantPublic[];
  tenantFeatures: Record<string, TenantFeatureState>;
}) {
  return (
    <section className="card" style={{ padding: 'var(--space-6)' }}>
      <h2 className="h3" style={{ color: 'var(--ink)', marginBottom: 'var(--space-2)' }}>
        feature comparison
      </h2>
      <p className="field-help" style={{ marginBottom: 'var(--space-4)' }}>
        Read-only snapshot of each tenant’s current feature flags. Unavailable means Cortex could
        not verify that tenant’s settings; it does not mean off.
      </p>
      <section
        {...keyboardScrollableRegionProps}
        aria-label="Scrollable feature comparison"
        style={{ overflowX: 'auto', border: '1px solid var(--rule)', borderRadius: 8 }}
      >
        <table
          aria-label="Feature comparison"
          style={{
            width: '100%',
            minWidth: 240 + clients.length * 112,
            borderCollapse: 'collapse',
            fontSize: 'var(--fs-small)',
          }}
        >
          <thead>
            <tr>
              <th scope="col" style={featureHeaderStyle}>
                feature
              </th>
              {clients.map((client) => (
                <th key={client.slug} scope="col" style={tenantHeaderStyle}>
                  {client.slug}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {FEATURE_FLAG_META.map((feature) => (
              <tr key={feature.key}>
                <th scope="row" title={feature.help} style={featureCellStyle}>
                  {feature.label}
                </th>
                {clients.map((client) => (
                  <td key={client.slug} style={stateCellStyle}>
                    <FeatureStateCell state={tenantFeatures[client.slug]} flag={feature.key} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </section>
  );
}

function FeatureStateCell({
  state,
  flag,
}: {
  state: TenantFeatureState | undefined;
  flag: keyof AdminFeatureFlags;
}) {
  if (!state) return <span style={{ color: 'var(--muted)' }}>loading…</span>;
  if (state.status === 'unavailable') {
    return <span style={{ color: 'var(--muted)' }}>unavailable</span>;
  }
  const enabled = state.flags[flag];
  return (
    <span style={{ color: enabled ? 'var(--green)' : 'var(--muted)', fontWeight: 600 }}>
      {enabled ? 'on' : 'off'}
    </span>
  );
}

const sharedTableCellStyle = {
  padding: '10px 12px',
  borderBottom: '1px solid var(--rule)',
} as const;

// Safari does not consistently focus overflow regions without an explicit tab stop.
const keyboardScrollableRegionProps = { tabIndex: 0 } as const;

const featureHeaderStyle = {
  ...sharedTableCellStyle,
  minWidth: 240,
  textAlign: 'left',
  color: 'var(--muted)',
  background: 'var(--surface)',
} as const;

const tenantHeaderStyle = {
  ...sharedTableCellStyle,
  minWidth: 112,
  textAlign: 'center',
  color: 'var(--ink)',
  background: 'var(--surface)',
  fontFamily: 'var(--font-mono)',
  letterSpacing: 'var(--tracking-mono)',
  textTransform: 'uppercase',
} as const;

const featureCellStyle = {
  ...sharedTableCellStyle,
  textAlign: 'left',
  color: 'var(--ink)',
  fontWeight: 500,
} as const;

const stateCellStyle = {
  ...sharedTableCellStyle,
  textAlign: 'center',
  fontFamily: 'var(--font-mono)',
  textTransform: 'uppercase',
} as const;

function ClientRow({
  client,
  onRetry,
  onSelect,
  retrying,
}: {
  client: ClientTenantPublic;
  onRetry: () => void;
  onSelect: () => void;
  retrying: boolean;
}) {
  const subdomain = `${client.slug}.acelcortex.ai`;
  // Card itself is non-interactive; the inner button overlays the
  // header+meta+error region for click/keyboard. Retry is a sibling at the
  // bottom — avoids nesting <button> inside <button> (HTML spec violation +
  // biome's a11y/useSemanticElements rule).
  return (
    <div className="card" style={{ padding: 'var(--space-6)' }}>
      <button
        type="button"
        onClick={onSelect}
        aria-label={`Open ${client.displayName} detail`}
        style={{
          all: 'unset',
          display: 'block',
          width: '100%',
          cursor: 'pointer',
          color: 'inherit',
          font: 'inherit',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 'var(--space-4)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
            <StatusDot status={client.status} />
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <span style={{ fontSize: 'var(--fs-h3)', fontWeight: 500, color: 'var(--ink)' }}>
                {client.displayName}
              </span>
              <span
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 'var(--fs-mono-sm)',
                  letterSpacing: 'var(--tracking-mono)',
                  color: 'var(--muted)',
                }}
              >
                {subdomain}
              </span>
            </div>
          </div>
          <span
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 'var(--fs-mono-sm)',
              letterSpacing: 'var(--tracking-mono)',
              textTransform: 'uppercase',
              color: statusColor(client.status),
            }}
          >
            {client.status}
          </span>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 'var(--space-3)',
          }}
        >
          <Meta label="PMS" value={providerLabel(client.pmsProvider)} />
          {client.smsEnabled ? (
            <>
              <Meta label="from number" value={client.twilioFromNumber} />
              <Meta label="phone sid" value={`PN…${client.twilioPhoneNumberSidTail}`} />
            </>
          ) : (
            <>
              <Meta label="sms" value="disabled" />
              <Meta label="channel" value="Telegram-only" />
            </>
          )}
        </div>

        {client.errorText && (
          <pre
            style={{
              marginTop: 'var(--space-4)',
              padding: 'var(--space-3)',
              background: 'var(--paper-2)',
              border: '1px solid var(--rule)',
              borderRadius: '6px',
              fontSize: 'var(--fs-mono-sm)',
              fontFamily: 'var(--font-mono)',
              color: 'var(--ink)',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              maxHeight: 200,
              overflow: 'auto',
            }}
          >
            {client.errorText}
          </pre>
        )}
      </button>

      {client.status === 'failed' && (
        <button
          type="button"
          onClick={onRetry}
          disabled={retrying}
          className="btn-secondary"
          style={{ marginTop: 'var(--space-4)' }}
        >
          {retrying ? 'retrying…' : 'retry provision'}
        </button>
      )}
    </div>
  );
}

function providerLabel(provider: ClientTenantPublic['pmsProvider']): string {
  switch (provider) {
    case 'buildium':
      return 'Buildium';
    case 'rentvine':
      return 'Rentvine';
    case 'appfolio':
      return 'AppFolio';
  }
}

function StatusDot({ status }: { status: ClientStatus }) {
  return (
    <span
      aria-label={status}
      style={{
        width: 8,
        height: 8,
        borderRadius: '50%',
        background: statusColor(status),
        display: 'inline-block',
      }}
    />
  );
}

function statusColor(status: ClientStatus): string {
  switch (status) {
    case 'live':
      return 'var(--green)';
    case 'failed':
      return '#c84a3b';
    case 'configuring':
    case 'bootstrapping':
      return '#d2a04f';
    case 'archived':
      return 'var(--muted)';
  }
}

function Meta({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
      <span
        style={{
          fontFamily: 'var(--font-mono)',
          fontSize: 'var(--fs-mono-sm)',
          letterSpacing: 'var(--tracking-mono)',
          textTransform: 'uppercase',
          color: 'var(--muted)',
        }}
      >
        {label}
      </span>
      <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--ink)' }}>{value}</span>
    </div>
  );
}
